/**
 * Console listing components, client side.
 *
 * Half the listings on the site are rendered by Django and half by a fetch:
 * the feed's first page and hubs come from `partials/console_tile.html`, and
 * everything after that comes from here. A visitor sees both — the server's
 * cards are on screen while the client's replace them — so this module emits
 * the same markup those partials do, and `test_console_cards.py` renders both
 * over the same fixture and diffs them. If you change one, change the other;
 * the test will tell you if you forgot.
 *
 * Deliberately free of the DOM: every function takes data and returns a
 * string. That is what lets the parity test import it under Node, and it keeps
 * the renderers out of the business of deciding when to draw.
 *
 * Escaping is not optional here. Listing titles are seller-authored text from
 * eBay, and they arrive with quotes, angle brackets and the occasional stray
 * tag in them.
 */

import { trust as sellerTrust } from './trust-score.js';
import { sellerNumbers } from './floor-state.js';

/** Currency prefixes, matching products/product_cards.py::_CURRENCY_PREFIXES. */
const CURRENCY_PREFIXES = {
  USD: '$', GBP: '£', EUR: '€', JPY: '¥',
  CAD: 'CA$', AUD: 'A$', NZD: 'NZ$', HKD: 'HK$', SGD: 'SGD ',
  CNY: 'CN¥', MXN: 'MX$', BRL: 'R$', TWD: 'NT$',
  INR: '₹', KRW: '₩', PHP: '₱', ILS: '₪',
};

/** How recently a listing must have appeared to wear the "New" flag. */
export const NEW_FLAG_HOURS = 24;

/**
 * Django's `escape()`, character for character.
 *
 * Not the usual `&#39;` for an apostrophe: Django emits `&#x27;`, and the
 * parity test diffs these renderers against the templates byte for byte. Half
 * the listing titles on eBay contain an apostrophe.
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/** "$1,149.00" — same output as card_price_label() server-side. */
export function priceLabel(price, currency) {
  const value = Number(price);
  if (!Number.isFinite(value)) return null;
  const code = String(currency || 'USD').trim().toUpperCase() || 'USD';
  const prefix = CURRENCY_PREFIXES[code] || `${code} `;
  return prefix + value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * How this price compares with the typical asking price, or null.
 *
 * Mirrors products/product_cards.py::price_delta, including the truncation
 * toward zero: a 12.8% gap reads 12% on both sides of the wire or the number
 * changes when the client feed lands.
 *
 * The phrase is fixed at "vs typical ask" and the comparison is always against
 * the median ask we observed — never sold prices, never MSRP. See
 * docs/EBAY-DATA-RETENTION.md.
 */
export function priceDelta(price, context) {
  if (!context || price === null || price === undefined) return null;
  const median = Number(context.median);
  const listing = Number(price);
  if (!Number.isFinite(median) || !Number.isFinite(listing) || median <= 0) return null;
  const pct = Math.trunc((listing / median - 1) * 100);
  if (pct === 0) return null;
  const up = pct > 0;
  const magnitude = Math.abs(pct);
  return {
    pct: magnitude,
    up,
    arrow: up ? '▲' : '▼',
    text: `${magnitude}% vs typical ask`,
    label: `${magnitude}% ${up ? 'above' : 'below'} the typical asking price`,
  };
}

/**
 * "99.8% · 2,310", or null when the seller carries no rating.
 *
 * The return policy used to be the third part. It is gone from the card: every
 * floor above "off" already requires returns, so on a floored feed the word was
 * on every card and said nothing, and the shipping pill is the fact a buyer
 * actually reads at this size. `short` is kept because both renderers and the
 * card dict still carry a long and a short form — they are now the same string.
 */
export function sellerLine(item, short = false) {  // eslint-disable-line no-unused-vars
  const { feedback, reviews } = sellerNumbers(item);
  if (!Number.isFinite(feedback) || !Number.isFinite(reviews)) return null;
  // %g in Python: 100 prints as "100", 99.80 as "99.8".
  return [`${Number(feedback.toFixed(4))}%`, Math.trunc(reviews).toLocaleString('en-US')]
    .join(' · ');
}

/**
 * A card price as a number, or null.
 *
 * The two halves of the feed disagree about the type: bot-discovery rows carry
 * `"1149.00"`, legacy Product rows carry `"$129.99"`. Only the sort reads this,
 * so a symbol it cannot parse is a card that sorts last, never a wrong number.
 */
export function numericPrice(price) {
  if (price === null || price === undefined) return null;
  const value = Number(String(price).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(value) ? value : null;
}

/**
 * Does this listing ship free?
 *
 * Two spellings reach here and both mean the same thing: the feed rows carry
 * `shipping_display` straight off the variant ("Free", "$4.99", "See listing"),
 * and Trusted Finder's live eBay rows carry `shipping_cost`. Anything that is
 * not exactly "free" is left alone — "Calculated" is not a promise.
 */
export function freeShipping(item) {
  const raw = item.shippingDisplay ?? item.shipping_display
    ?? item.shippingCost ?? item.shipping_cost ?? '';
  return String(raw).trim().toLowerCase() === 'free';
}

/** 'new' for a listing first seen in the last day, else null. See card_flag(). */
export function cardFlag(item, now = Date.now()) {
  const created = item.addedAt || item.added_at || item.createdAt || item.created_at;
  if (!created) return null;
  const seen = Date.parse(created);
  if (!Number.isFinite(seen)) return null;
  return (now - seen) <= NEW_FLAG_HOURS * 3600 * 1000 ? 'new' : null;
}

/**
 * A feed item, in the shape the renderers below want.
 *
 * `searchData.items` and the raw API rows disagree about several of these
 * names, and neither matches the server's card dict, so the normalising
 * happens once here rather than inside every renderer.
 */
export function toCard(item, options = {}) {
  const price = item.price ?? item.price_label ?? null;
  const seller = sellerNumbers(item);
  return {
    url: options.url || item.url || '#',
    product_id: item.productId || item.product_id || '',
    // `name` is the card contract; a live eBay item calls the same field
    // `title`. Overridable for the same reason `url` and `imageUrl` are: the
    // Trusted Finder feeds this raw Browse-API payloads rather than catalog
    // rows, and it is the caller that knows which dialect it is holding.
    name: options.name || item.name || item.title || '',
    // `imageUrl` is the caller's already-resolved, already-vetted src. This
    // module cannot vet one itself without pulling in the DOM.
    image_url: options.imageUrl || item.imageUrl || item.image_url || '',
    condition: item.conditionProvenanceLabel || item.condition_provenance_label
      || item.condition || '',
    price_label: item.price_label || priceLabel(price, item.currency),
    price_context: item.priceContext || item.price_context || null,
    delta: priceDelta(price, item.priceContext || item.price_context || null),
    trust: sellerTrust(item),
    seller_line: sellerLine(item),
    seller_short: sellerLine(item, true),
    free_shipping: options.freeShipping ?? freeShipping(item),
    // Numbers, not the rendered string: console-sort.js orders the Seller
    // column on them, and parsing "99.8% · 2,310" back apart to do it would
    // put a locale-formatted thousands separator in a comparator.
    seller_feedback: Number.isFinite(seller.feedback) ? seller.feedback : null,
    seller_reviews: Number.isFinite(seller.reviews) ? seller.reviews : null,
    flag: cardFlag(item, options.now),
    // Not rendered by either renderer, and deliberately: the sort in
    // console-sort.js orders cards, and re-parsing "$1,149.00" back into a
    // number to do it would put the currency prefix table in a comparator.
    price_value: numericPrice(price),
    // Trusted Finder's results are live eBay listings that have no page on
    // this site yet, so its cards link off-platform. The component is the
    // same; only where the link goes and what it announces change.
    external: !!options.external,
  };
}

/**
 * The attributes an off-platform card carries, and nothing when it is not one.
 *
 * Mirrors the `{% if p.external %}` in both partials -- test_console_cards.py
 * diffs them.
 */
function externalAttrs(card) {
  return card.external ? ' target="_blank" rel="noopener noreferrer"' : '';
}

/** Said inside the link, because "opens on eBay" is part of where it goes. */
function externalNote(card) {
  return card.external ? '<span class="vh"> — opens on eBay</span>' : '';
}

export function ringHtml(trust) {
  return `<span class="ring t${trust.tier}" style="--v:${trust.score}" role="img"`
    + ` aria-label="${escapeHtml(trust.label)}"><b>${trust.score}</b></span>`;
}

function meterHtml(trust) {
  const lit = (n) => (trust.tier >= n ? ' class="on"' : ' class=""');
  return `<span class="meter t${trust.tier}" aria-hidden="true">`
    + `<i${lit(1)}></i><i${lit(2)}></i><i${lit(3)}></i></span>`;
}

/**
 * `short` drops "vs typical ask" from the visible text, because in the Rows
 * view the column header already says it and the phrase wraps to two lines in
 * a 120px column. The accessible sentence is unchanged either way -- a screen
 * reader gets no column header on a list of links.
 */
function deltaHtml(card, short = false) {
  if (!card.delta) return '';
  const receipt = escapeHtml((card.price_context && card.price_context.receipt) || '');
  const visible = short ? `${card.delta.pct}%` : escapeHtml(card.delta.text);
  return `<span class="d${card.delta.up ? ' up' : ''}" title="${receipt}">`
    + `<span aria-hidden="true">${card.delta.arrow} ${visible}</span>`
    + `<span class="vh">${escapeHtml(card.delta.label)}</span></span>`;
}

/**
 * The free-shipping pill, or nothing.
 *
 * Only ever the affirmative: a listing whose shipping we do not know reads the
 * same as one that charges for it, because "no free-shipping pill" is not a
 * claim and "paid shipping" would be one we cannot always support.
 */
function shippingHtml(card) {
  return card.free_shipping ? '<span class="ship">Free shipping</span>' : '';
}

/** One tile. Mirrors partials/console_tile.html. */
export function tile(card) {
  const flag = card.flag
    ? `<span class="flag ${card.flag}">${escapeHtml(card.flag.charAt(0).toUpperCase() + card.flag.slice(1))}</span>`
    : '';
  const condition = card.condition ? `<div class="c">${escapeHtml(card.condition)}</div>` : '';
  const shipping = shippingHtml(card);
  const price = card.price_label ? `<span class="p">${escapeHtml(card.price_label)}</span>` : '';
  // No ring without seller numbers, and never a zero-score one: the gap is
  // ours, not the seller's. See partials/console_tile.html.
  const seller = card.trust
    ? `${ringHtml(card.trust)}<span>${escapeHtml(card.seller_line)}</span>`
    : '<span class="tag">Seller not rated</span>';
  // data-ssr-card is on the client render too, and deliberately: it marks
  // "these are real listings, not a placeholder", which renderItems() reads
  // before deciding whether to draw a skeleton. Emitting it from only one of
  // the two renderers would also break the parity test for no gain.
  return `<a class="tile" href="${escapeHtml(card.url)}"${externalAttrs(card)} data-ssr-card`
    + ` data-product-card-id="${escapeHtml(card.product_id || '')}">`
    + flag
    + `<img class="img" src="${escapeHtml(card.image_url)}" alt="" loading="lazy" width="96" height="96">`
    + `<div class="b"><div class="t">${escapeHtml(card.name)}${externalNote(card)}</div>${condition}${shipping}`
    + `<div class="pr">${price}${deltaHtml(card)}</div></div>`
    + `<div class="ft"><span class="s">${seller}</span>`
    + (card.trust ? meterHtml(card.trust) : '')
    + '</div></a>';
}

/** One row. Mirrors partials/console_row.html. */
export function row(card) {
  const shipping = shippingHtml(card);
  const sub = (card.condition || shipping)
    ? `<div class="sub">${escapeHtml(card.condition)}${shipping}</div>`
    : '';
  const trustCell = card.trust ? ringHtml(card.trust) : '<span class="tag">Not rated</span>';
  return `<a class="row" href="${escapeHtml(card.url)}"${externalAttrs(card)} data-ssr-card`
    + ` data-product-card-id="${escapeHtml(card.product_id || '')}">`
    + `<img class="img" src="${escapeHtml(card.image_url)}" alt="" loading="lazy" width="48" height="48">`
    + `<span><div class="t">${escapeHtml(card.name)}${externalNote(card)}</div>${sub}</span>`
    + `<span class="p">${escapeHtml(card.price_label || '')}</span>`
    + `<span>${deltaHtml(card, true)}</span>`
    + `<span class="sl">${escapeHtml(card.seller_short || '')}</span>`
    + `<span class="tr">${trustCell}</span>`
    + '</a>';
}

/**
 * The columns of the Rows view, in order, and the sort key each one carries.
 *
 * One list rather than a label in the header and a key in the comparator: a
 * column the header can sort by but console-sort.js has no comparator for is a
 * button that does nothing, and the two used to be able to drift apart.
 */
export const ROW_COLUMNS = [
  { key: 'name', label: 'Listing' },
  { key: 'price', label: 'Price' },
  { key: 'delta', label: 'vs typical ask' },
  { key: 'seller', label: 'Seller' },
  { key: 'trust', label: 'Trust', cls: 'tr' },
];

/**
 * The Rows view, header included. Mirrors partials/console_rows.html.
 *
 * `sort` turns the header into buttons: `{key, dir}` for the column in force.
 * Without it the header is the inert, aria-hidden label strip every other
 * surface draws — the Feed is the only page that re-renders itself on a click
 * here, and a header that looks sortable everywhere else would be a lie.
 */
export function rows(cards, sort = null) {
  const cells = ROW_COLUMNS.map((column) => {
    const cls = column.cls ? ` class="${column.cls}"` : '';
    if (!sort) return `<span${cls}>${column.label}</span>`;
    const on = sort.key === column.key;
    const dir = on ? sort.dir : '';
    return `<span${cls}><button type="button" class="sortbtn${on ? ' on' : ''}"`
      + ` data-sortkey="${column.key}" data-sortdir="${dir}"`
      + ` aria-pressed="${on ? 'true' : 'false'}">${column.label}`
      + `<span class="ar" aria-hidden="true">${on ? (dir === 'desc' ? '▼' : '▲') : ''}</span>`
      + `<span class="vh">${on ? `, sorted ${dir === 'desc' ? 'descending' : 'ascending'}` : ', not sorted'}</span>`
      + '</button></span>';
  }).join('');
  return `<div class="rows"><div class="row h"${sort ? '' : ' aria-hidden="true"'}>`
    + `<span></span>${cells}`
    + '</div>'
    + cards.map(row).join('')
    + '</div>';
}

/** The Tiles view. */
export function grid(cards) {
  return `<div class="grid">${cards.map(tile).join('')}</div>`;
}
